import { Breadcrumb, VideoPlayer } from 'lucid-components-react';
import React, { useContext } from 'react';

import LayoutContext from '../../contexts/layoutContext';
import {
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm
} from '../../generated/graphql';
import Link from '../link/link';
import './sunrise-header.scss';
import useMarketoLink from '../../hooks/use-marketo-link';

interface Props {
  header: string;
  subheader?: string;
  backgroundImage?: string;
  hasBackgroundImage?: boolean;
  smallBgImage?: boolean;
  breadCrumbHub?: string;
  breadCrumbPage?: string;
  fullSunrise?: boolean;
  pageType?: string;
  videoLink?: string;
  buttons?: Drupal_ParagraphCtaButton[];
}

const SunriseHeader = ({
  header,
  subheader = '',
  buttons,
  backgroundImage = '',
  hasBackgroundImage,
  smallBgImage,
  breadCrumbHub = '',
  breadCrumbPage = '',
  fullSunrise,
  videoLink = '',
  pageType = ''
}: Props) => {
  const { language, video } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);

  const SOLUTIONS = 'Solutions';

  return (
    <section className="sunrise-header">
      <div
        className={`sunrise-header__hero sunrise-header__hero--sunrise ${
          fullSunrise
            ? 'sunrise-header__hero--spacious'
            : 'sunrise-header__hero--skinny'
        }  ${
          hasBackgroundImage && !smallBgImage
            ? 'sunrise-header__hero--with-overlap sunrise-header__hero--spacious'
            : 'sunrise-header__border-bottom'
        } ${breadCrumbHub ? 'container__column' : ''}
        ${pageType === 'individualRole' ? 'tablet-pull-up-lg--before' : ''}`}
      >
        <div className="lucid-container  lucid-container--flex">
          <div
            className={`${
              fullSunrise
                ? 'padding-b-md padding-t-sm'
                : 'container--half-desktop'
            }
              ${
                pageType === 'individualRole'
                  ? 'container__column--full-width--tablet'
                  : ''
              }
            `}
          >
            {breadCrumbHub && (
              <Breadcrumb
                inverse
                items={[
                  <Link
                    key={breadCrumbHub}
                    secondary
                    inverse
                    className="text--white"
                    to={
                      breadCrumbHub !== SOLUTIONS
                        ? `/solutions/${breadCrumbHub.toLowerCase()}`
                        : '/solutions'
                    }
                  >
                    {breadCrumbHub}
                  </Link>,
                  breadCrumbPage
                ]}
              />
            )}
            <h1
              className={`sunrise-header__tagline text--white ${
                fullSunrise ? 'padding-r-lg no-padding-tablet' : ''
              }`}
            >
              {header}
            </h1>
            {hasBackgroundImage && (
              <div className="subheader__container light-copy text--white">
                {subheader && (
                  <p className="callout-text padding-b-sm">{subheader}</p>
                )}
                {buttons && (
                  <div className="buttons-container">
                    {buttons.map((button, i) => {
                      const entity = button.fieldMarketo
                        ? (button.fieldMarketo as {
                            entity: Drupal_ParagraphMarketoForm;
                          })
                        : null;
                      const marketo = entity?.entity || null;
                      const { url, copy } = getMarketoLink(
                        marketo?.fieldMarketoId
                      )!;
                      return (
                        (marketo?.fieldMarketoId && (
                          <Link
                            to={url}
                            asButton
                            className="buttons-container__button"
                            key={
                              button.fieldButtonText ||
                              `sunrise-header-button-${i}`
                            }
                            primary={i === 0}
                            secondary={i !== 0}
                          >
                            {button.fieldButtonText || copy}
                          </Link>
                        )) ||
                        (button.fieldButtonLink && (
                          <Link
                            className="buttons-container__button"
                            key={
                              button.fieldButtonText ||
                              `sunrise-header-button-${i}`
                            }
                            to={button.fieldButtonLink}
                            asButton
                            primary={i === 0}
                            secondary={i !== 0}
                          >
                            {button.fieldButtonText}
                          </Link>
                        ))
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          {!hasBackgroundImage && (
            <>
              <div
                className={`${
                  fullSunrise
                    ? 'container__column--full-width'
                    : 'container--uneven--small-desktop'
                } ${
                  breadCrumbHub
                    ? 'container__column--full-width--tablet mobile-pull-reset'
                    : ''
                }`}
              >
                <div className="subheader__container light-copy">
                  {subheader && (
                    <p
                      className={`subheader__text padding-b-sm  ${
                        fullSunrise
                          ? 'subheader__text--full'
                          : 'subheader__text--half'
                      }`}
                    >
                      {subheader}
                    </p>
                  )}

                  {buttons && (
                    <div className="buttons-container">
                      {buttons.map((button, i) => {
                        const entity = button.fieldMarketo
                          ? (button.fieldMarketo as {
                              entity: Drupal_ParagraphMarketoForm;
                            })
                          : null;
                        const marketo = entity?.entity || null;
                        const { url, copy } = getMarketoLink(
                          marketo?.fieldMarketoId
                        )!;
                        return (
                          marketo?.fieldMarketoId && (
                            <Link
                              to={url}
                              asButton
                              className="buttons-container__button"
                              key={
                                button.fieldButtonText ||
                                `sunrise-header-button-${i}`
                              }
                              primary={i === 0}
                              secondary={i !== 0}
                            >
                              {button.fieldButtonText || copy}
                            </Link>
                          )
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {!fullSunrise && (
                <div className="container  container--no-border  layout  tablet-pull-up-lg  mobile-pull-reset">
                  <div className="layout__item  layout__item--12  border-b-light" />
                  <div className="layout__item  layout__item--1  relative">
                    <div className="down-arrow  hide-tablet" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {backgroundImage && (
          <div
            className={`sunrise-header__media ${
              videoLink ? 'sunrise-header__media--video' : ''
            }`}
          >
            {videoLink ? (
              <VideoPlayer
                className="sunrise-header__video lucid-modal-video"
                onClick={() => video.getVideoModal(videoLink)}
                placeholder={() => <img src={backgroundImage} alt={header} />}
              />
            ) : (
              <img
                className="sunrise-header__image"
                src={backgroundImage}
                alt={header}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default SunriseHeader;
